/**
 * Automation rules — DESIGN.md §14 (R28).
 *
 * "`automation_rules`: **trigger** (one of …) + ordered **conditions** (same
 * Filter objects as ViewConfig — reuse, don't reinvent) + ordered **actions**."
 *
 * The condition reuse is the load-bearing decision: conditions *are*
 * ViewConfig `Filter`s, evaluated by the same query engine, so "cards this
 * rule would fire on" and "cards this view shows" cannot disagree.
 */
import { z } from 'zod';
import { PRIORITIES } from '../enums.js';
import { filterSchema } from './view-config.js';
import { name, optionalText, uuidSchema } from './common.js';
import { userRefSchema } from './users.js';
/** §14.1 — the six triggers, exactly. */
export const automationTriggerSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('status_changed'),
        from: uuidSchema.nullish(),
        to: uuidSchema.nullish(),
    }),
    z.object({ type: z.literal('task_created') }),
    z.object({ type: z.literal('task_completed') }),
    z.object({
        type: z.literal('field_changed'),
        fieldId: uuidSchema,
        to: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
    }),
    z.object({ type: z.literal('due_date_approaching'), days: z.number().int().min(0).max(90) }),
    z.object({ type: z.literal('tag_added'), tagId: uuidSchema }),
]);
/** §14.2 — the action set. */
export const automationActionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('set_status'), statusId: uuidSchema }),
    z.object({ type: z.literal('set_assignee'), assigneeId: uuidSchema.nullable() }),
    z.object({
        type: z.literal('set_field'),
        fieldId: uuidSchema,
        value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]),
    }),
    z.object({ type: z.literal('add_tags'), tagIds: z.array(uuidSchema).min(1).max(50) }),
    z.object({ type: z.literal('remove_tags'), tagIds: z.array(uuidSchema).min(1).max(50) }),
    z.object({
        type: z.literal('add_collaborators'),
        userIds: z.array(uuidSchema).max(100).default([]),
        groupIds: z.array(uuidSchema).max(50).default([]),
    }),
    z.object({ type: z.literal('move_to_section'), sectionId: uuidSchema }),
    z.object({
        type: z.literal('add_comment'),
        // §14.2 — "template with `{{title}}`, `{{actor}}`, `{{status}}` vars".
        template: z.string().min(1).max(4000),
    }),
    z.object({
        type: z.literal('create_subtasks'),
        titles: z.array(name(500)).min(1).max(50),
    }),
    z.object({
        type: z.literal('notify'),
        userIds: z.array(uuidSchema).max(100).default([]),
        groupIds: z.array(uuidSchema).max(50).default([]),
        message: optionalText(500),
    }),
    z.object({ type: z.literal('set_due_date'), relativeDays: z.number().int().min(-365).max(365) }),
    z.object({ type: z.literal('set_priority'), priority: z.enum(PRIORITIES).nullable() }),
]);
export const automationRuleSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    projectId: uuidSchema.nullable(),
    trigger: automationTriggerSchema,
    conditions: z.array(filterSchema),
    actions: z.array(automationActionSchema),
    enabled: z.boolean(),
    /** §14.3 — "rule failures log + surface in Admin › Automations". */
    lastRunAt: z.string().nullable(),
    lastRunStatus: z.string().nullable(),
    lastRunError: z.string().nullable(),
    createdBy: userRefSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createAutomationRequestSchema = z
    .object({
    name: name(120),
    projectId: uuidSchema.nullish(),
    trigger: automationTriggerSchema,
    conditions: z.array(filterSchema).max(20).default([]),
    actions: z.array(automationActionSchema).min(1).max(20),
    // §8.2's seeded rules ship disabled; a hand-made one is on by default.
    enabled: z.boolean().default(true),
})
    .strict();
export const updateAutomationRequestSchema = z
    .object({
    name: name(120).optional(),
    trigger: automationTriggerSchema.optional(),
    conditions: z.array(filterSchema).max(20).optional(),
    actions: z.array(automationActionSchema).min(1).max(20).optional(),
    enabled: z.boolean().optional(),
})
    .strict();
/** §14.3 — "`POST /automations/:id/test` dry-runs against a card id". */
export const testAutomationRequestSchema = z.object({ taskId: uuidSchema }).strict();
export const automationTestResultSchema = z.object({
    /** Whether the trigger would have matched at all. */
    triggerMatches: z.boolean(),
    /** Whether every condition passes for this card. */
    conditionsPass: z.boolean(),
    /** Human-readable, in order — what would happen, without doing it. */
    wouldDo: z.array(z.string()),
    /** Why nothing would happen, when that is the answer. */
    reason: z.string().nullable(),
});
//# sourceMappingURL=automations.js.map